import { DesignIcon } from "./DesignIcon";
import { Show, type Accessor, type JSX } from "solid-js";
import { formatInteger, formatMilliseconds } from "../format";
import type { ApiMeta, CacheStatus } from "../types";

export function identifierLabel(value: string | undefined): string {
  if (!value) return "None";
  return value
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function TypeBadge(props: { type: string }): JSX.Element {
  return (
    <span
      class={`type-badge type-${props.type}`}
      aria-label={`${props.type} type`}
    >
      {props.type === "user" ? "U" : <DesignIcon name={props.type} />}
    </span>
  );
}

export function CacheTiming(props: {
  title?: string;
  status?: CacheStatus;
  elapsedMs?: number;
}): JSX.Element {
  return (
    <Show when={props.status || props.elapsedMs !== undefined}>
      <span
        title={props.title}
        class={`cache-timing cache-badge${
          props.status ? ` cache-badge--${props.status}` : ""
        }`}
      >
        <Show when={props.elapsedMs !== undefined}>
          <span class="cache-timing__duration">
            {formatMilliseconds(props.elapsedMs ?? 0)}ms
          </span>
        </Show>
        <Show when={props.status}>
          {(status) => (
            <span class="cache-timing__status">{status().toUpperCase()}</span>
          )}
        </Show>
      </span>
    </Show>
  );
}

export function MetaTiming(props: { meta?: ApiMeta }): JSX.Element {
  return (
    <CacheTiming
      title={
        props.meta
          ? `Request: ${props.meta.requestId} · Basis: ${props.meta.basis?.id ?? props.meta.revision}`
          : undefined
      }
      status={props.meta?.cacheStatus}
      elapsedMs={props.meta?.elapsedMs}
    />
  );
}

/** The approved preview's disclosure artwork, kept independent of font glyphs. */
export function DisclosureIcon(props: { expanded: boolean }): JSX.Element {
  return <svg class="disclosure-icon" viewBox="0 0 18 18" aria-hidden="true">
    <rect x="2" y="2" width="14" height="14" rx="3" />
    <path d={`M5 9h8${props.expanded ? "" : "M9 5v8"}`} />
  </svg>;
}

export function DisclosureButton(props: {
  expanded: boolean;
  controls: string;
  onClick: () => void;
  children: JSX.Element;
}): JSX.Element {
  return (
    <button
      type="button"
      class="group-card__toggle"
      aria-expanded={props.expanded}
      aria-controls={props.controls}
      onClick={() => props.onClick()}
    >
      <span class="group-card__caret" aria-hidden="true">
        <DisclosureIcon expanded={props.expanded} />
      </span>
      {props.children}
    </button>
  );
}

export function Pagination(props: {
  query?: string;
  page: number;
  canPrevious: boolean;
  canNext: boolean;
  busy?: boolean;
  busyAction?: "first" | "previous" | "next";
  first: () => void;
  previous: () => void;
  next: () => void;
}): JSX.Element {
  const busy = () => props.busy || Boolean(props.busyAction);
  return (
    <div class="pagination-controls" aria-label="Pagination" aria-busy={busy()}>
      <Show when={props.query}><code class="query-kind">{props.query}</code></Show>
      <button
        type="button"
        class="pagination-button"
        disabled={busy() || !props.canPrevious}
        aria-busy={props.busyAction === "first"}
        onClick={() => props.first()}
      >
        <Show when={props.busyAction === "first"}>
          <ButtonSpinner />
        </Show>
        First
      </button>
      <button
        type="button"
        class="pagination-button"
        disabled={busy() || !props.canPrevious}
        aria-busy={props.busyAction === "previous"}
        onClick={() => props.previous()}
      >
        <Show when={props.busyAction === "previous"}>
          <ButtonSpinner />
        </Show>
        Prev
      </button>
      <span class="pagination-page" aria-label={`Page ${props.page}`} />
      <button
        type="button"
        class="pagination-button"
        disabled={busy() || !props.canNext}
        aria-busy={props.busyAction === "next"}
        onClick={() => props.next()}
      >
        <Show when={props.busyAction === "next"}>
          <ButtonSpinner />
        </Show>
        Next
      </button>
    </div>
  );
}

export function ButtonSpinner(): JSX.Element {
  return <span class="button-spinner" aria-hidden="true" />;
}

export function ErrorBlock(props: {
  error: unknown;
  label?: string;
  retry?: () => void;
  secondary?: { label: string; action: () => void };
}): JSX.Element {
  const message = () =>
    props.error instanceof Error ? props.error.message : String(props.error);
  return (
    <div class="error-block" role="alert">
      <div class="error-block__copy">
        <strong>{props.label ?? "Request failed"}</strong>
        <span>{message()}</span>
      </div>
      <div class="error-block__actions">
        <Show when={props.secondary}>
          {(secondary) => (
            <button
              type="button"
              class="retry-button"
              onClick={() => secondary().action()}
            >
              {secondary().label}
            </button>
          )}
        </Show>
        <Show when={props.retry}>
          <button
            type="button"
            class="retry-button"
            onClick={() => props.retry?.()}
          >
            Retry
          </button>
        </Show>
      </div>
    </div>
  );
}

export function InlineLoading(props: { label: string }): JSX.Element {
  return (
    <span
      class="inline-loading"
      role="status"
      aria-live="polite"
      aria-label={props.label}
      title={props.label}
    >
      <span class="button-spinner" aria-hidden="true" />
    </span>
  );
}

export function InlineError(props: { label: string }): JSX.Element {
  return (
    <span class="inline-error" role="alert">
      {props.label}
    </span>
  );
}

export function LoadingBlock(props: {
  label: string;
  refreshing?: Accessor<boolean>;
}): JSX.Element {
  const status = () =>
    props.refreshing?.()
      ? `Refreshing ${props.label}`
      : `Loading ${props.label}`;
  return (
    <div
      class="loading-block"
      role="status"
      aria-live="polite"
      aria-label={status().toUpperCase()}
      title={status().toUpperCase()}
    >
      <span class="button-spinner" aria-hidden="true" />
    </div>
  );
}

export function EmptyState(props: { children: JSX.Element }): JSX.Element {
  return <div class="empty-state">{props.children}</div>;
}
